import { useState, useEffect } from "react"
import StarBorderIcon from "@mui/icons-material/StarBorder"
import StarIcon from "@mui/icons-material/Star"
import NorthIcon from "@mui/icons-material/North"
import { formatPurchaseDate, purchaseScheduleLabel } from "../../../util/purchaseDate"

const EditProductForm = ({ currentProduct, categories, setUpdated }) => {
  const product = currentProduct

  /* ================================
     STATE (MATCHES NEW PRODUCT FORM)
  ================================= */
  const [inputValue, setInputValue] = useState({
    productName: "",
    productPrice: "",
    productCompPrice: "",
    productDescription: "",
    productImage: "",
    productMinQty: "",
    productPopularity: "",
    productWeight: "",
    productLength: "",
    productWidth: "",
    productHeight: "",
    merchantName: "",
    merchantEmail: "",
    merchantWebsite: "",
    merchantPhone: "",
  })

  const [featured, setFeatured] = useState(false)
  const [mostFilled, setMostFilled] = useState(false)
  const [category, setCategory] = useState("")
  const [subCategory, setSubcategory] = useState("")


  const {
    productName,
    productPrice,
    productCompPrice,
    productDescription,
    productImage,
    productMinQty,
    productPopularity,
    productWeight,
    productLength,
    productWidth,
    productHeight,
    merchantName,
    merchantEmail,
    merchantWebsite,
    merchantPhone,
  } = inputValue

  /* ================================
     SYNC PRODUCT INTO FORM
  ================================= */
  useEffect(() => {
    if (!product) return

    setInputValue({
      productName: product.name || "",
      productPrice: product.price !== undefined ? String(product.price) : "",
      productCompPrice:
        product.comp_price !== undefined ? String(product.comp_price) : "",
      productDescription: product.description || "",
      productImage: product.image || "",
      productMinQty:
        product.min_qty !== undefined ? String(product.min_qty) : "",
      productPopularity:
        product.popularity !== undefined ? String(product.popularity) : "",
      productWeight: product.weight !== undefined ? String(product.weight) : "",
      productLength: product.length !== undefined ? String(product.length) : "",
      productWidth: product.width !== undefined ? String(product.width) : "",
      productHeight: product.height !== undefined ? String(product.height) : "",
      merchantName: product.merchant_info?.name || "",
      merchantEmail: product.merchant_info?.email || "",
      merchantWebsite: product.merchant_info?.website || "",
      merchantPhone: product.merchant_info?.phone_number || "",
    })

    setCategory(product.category || "")
    setSubcategory(product.subCategory || "")
    setFeatured(!!product.featured)
    setMostFilled(!!product.mostFilled)
  }, [product])

  /* ================================
     INPUT CHANGE
  ================================= */
  const handleOnChange = (e) => {
    const { name, value } = e.target
    setInputValue((prev) => ({ ...prev, [name]: value }))
  }

  /* ================================
     SUBMIT (STRICT, NO FAKE DEFAULTS)
  ================================= */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!productName.trim()) return alert("Product name is required")
    if (!productImage.trim()) return alert("Product image is required")
    if (!productPrice || isNaN(productPrice))
      return alert("Valid price is required")
    if (!category) return alert("Category is required")

    const payload = {
      name: productName.trim(),
      price: Number(productPrice),
      category,
      subCategory,
      description: productDescription.trim(),
      image: productImage.trim(),
      featured,
      mostFilled,

      ...(productCompPrice !== "" && {
        comp_price: Number(productCompPrice),
      }),

      ...(productMinQty !== "" && {
        min_qty: Number(productMinQty),
      }),

      ...(productPopularity !== "" && {
        popularity: Number(productPopularity),
      }),

      ...(productWeight !== "" && {
        weight: Number(productWeight),
      }),

      ...(productLength !== "" && {
        length: Number(productLength),
      }),

      ...(productWidth !== "" && {
        width: Number(productWidth),
      }),

      ...(productHeight !== "" && {
        height: Number(productHeight),
      }),

      merchant_info: {
        name: merchantName.trim(),
        email: merchantEmail.trim(),
        website: merchantWebsite.trim(),
        phone_number: merchantPhone.trim(),
      },
    }

    console.log(payload)

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/products/${product.product_id}`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    )

    if (!res.ok) {
      const errorData = await res.json().catch(() => null)
      console.error("Update error:", errorData)
      alert(errorData?.error || errorData?.message || "Update failed")
      return
    }

    setUpdated?.(true)
    alert("Product successfully updated!")
  }

  const toggleFeatured = () => setFeatured((p) => !p)
  const toggleMostFilled = () => setMostFilled((p) => !p)

  /* ================================
     UI (FULL ORIGINAL + NEW FEATURE)
  ================================= */

  return (
    <div className="NewProductForm w-100 d-flex flex-column align-items-center">
      <div className="col-12 text-start ms-3">
        <div className="col-10 py-2 mb-4 mx-auto">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Name</label>

              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <input
                    type="text"
                    className="form-control"
                    name="productName"
                    value={productName}
                    onChange={handleOnChange}
                  />
                </div>

                {/* Featured toggle */}
                <button
                  type="button"
                  className="btn border-0 bg-transparent ms-2"
                  onClick={toggleFeatured}
                  style={{ fontSize: "28px", cursor: "pointer" }}
                  title={featured ? "Unmark Featured" : "Mark as Featured"}
                >
                  {featured ? (
                    <StarIcon style={{ fontSize: 28, color: "#ffb300" }} />
                  ) : (
                    <StarBorderIcon style={{ fontSize: 28 }} />
                  )}
                </button>

                {/* Most-filled toggle */}
                <button
                  type="button"
                  className="btn border-0 bg-transparent ms-2"
                  onClick={toggleMostFilled}
                  style={{ fontSize: 28, cursor: "pointer" }}
                  title={
                    mostFilled ? "Unmark Most Filled" : "Mark as Most Filled"
                  }
                >
                  {mostFilled ? (
                    <NorthIcon style={{ fontSize: 28, color: "#ffb300" }} />
                  ) : (
                    <NorthIcon style={{ fontSize: 28 }} />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-3 row gx-2">
              {/* Price */}
              <div className="col-3 text-center">
                <label className="form-label">Price</label>
                <div className="input-group">
                  <span className="input-group-text fs-5">₱</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="form-control fs-5"
                    name="productPrice"
                    value={productPrice}
                    onChange={handleOnChange}
                  />
                </div>
              </div>

              {/* Compared Price */}
              <div className="col-3 text-center">
                <label className="form-label">Compared Price</label>
                <div className="input-group">
                  <span className="input-group-text fs-5">₱</span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="form-control fs-5"
                    name="productCompPrice"
                    value={productCompPrice}
                    onChange={handleOnChange}
                  />
                </div>
              </div>

              {/* Category */}
              {/* Category */}
              <div className="col-3 text-center">
                <label className="form-label">Category</label>

                <select
                  className="form-select form-select-lg"
                  value={category || ""}
                  onChange={(e) => {
                    setCategory(e.target.value)
                    setSubcategory("") // reset subcategory when category changes
                  }}
                >
                  {/* Fallback for existing DB value not in categories */}
                  {category && !categories.some((c) => c.name === category) && (
                    <option value={category}>{category} </option>
                  )}

                  {/* Placeholder */}
                  {!category && <option value="">Select Category</option>}

                  {/* Valid categories */}
                  {categories.map((c) => (
                    <option key={c._id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subcategory */}
              <div className="col-3 text-center">
                <label className="form-label">Subcategory</label>

                <select
                  className="form-select form-select-lg"
                  value={subCategory || ""}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!category}
                >
                  {/* Fallback for invalid existing subcategory */}
                  {subCategory &&
                    !categories
                      .find((c) => c.name === category)
                      ?.subCategories?.includes(subCategory) && (
                      <option value={subCategory}>{subCategory} </option>
                    )}

                  {/* Placeholder */}
                  {!subCategory && <option value="">Select Subcategory</option>}

                  {/* Valid subcategories */}
                  {categories
                    .find((c) => c.name === category)
                    ?.subCategories?.map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="productDescription" className="form-label">
                Description
              </label>
              <textarea
                type="text"
                rows="5"
                className="form-control"
                name="productDescription"
                value={productDescription}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="productPopularity" className="form-label">
                Popularity (0-100)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                className="form-control"
                name="productPopularity"
                value={productPopularity}
                onChange={handleOnChange}
              />
              <div className="form-text">
                How commonly restaurants buy this. Higher numbers show first on the Products page.
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="productImage" className="form-label">
                Image URL
              </label>
              <input
                type="text"
                className="form-control"
                name="productImage"
                value={productImage}
                onChange={handleOnChange}
              />
            </div>

            <div className="mb-3 row">
              <div className="col-4 text-center">
                <label htmlFor="productMinQty" className="form-label">
                  Min Qty for Order
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="productMinQty"
                    value={productMinQty}
                    onChange={handleOnChange}
                  />
                  <span className="input-group-text fs-6">
                    {Number(productMinQty) > 1 ? "cases/kilos" : "case/kilo"}
                  </span>
                </div>
              </div>
              <div className="col-4 text-center">
                <label htmlFor="productWeight" className="form-label">
                  Weight
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control fs-5"
                    name="productWeight"
                    value={productWeight}
                    onChange={handleOnChange}
                  />
                  <span className="input-group-text fs-6">
                    {Number(productWeight) > 1 ? "kgs" : "kg"}
                  </span>
                </div>
              </div>
              <div className="col-4 text-center">
                <label className="form-label">Purchase Date</label>
                <div className="form-control bg-light">
                  {purchaseScheduleLabel(category)}
                  <div className="small text-muted">
                    Next: {formatPurchaseDate(category)}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <div className="row gx-2 mt-1">
                <div className="col-4 text-center">
                  <label htmlFor="productLength" className="form-label">
                    Length
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productLength"
                      value={productLength}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
                <div className="col-4 text-center">
                  <label htmlFor="productWidth" className="form-label">
                    Width
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productWidth"
                      value={productWidth}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
                <div className="col-4 text-center">
                  <label htmlFor="productHeight" className="form-label">
                    Height
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control fs-5"
                      name="productHeight"
                      value={productHeight}
                      onChange={handleOnChange}
                    />
                    <span className="input-group-text fs-6">(cm)</span>
                  </div>
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              Update Product
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditProductForm
